import React, { useState, useEffect } from "react";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

const Navbar = () => {
    const [nav, setNav] = useState(false);
    const [active, setActive] = useState("home");
    const [shrink, setShrink] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleScroll = (id) => {
        const doScroll = () => {
            gsap.to(window, {
                duration: 1,
                scrollTo: `#${id}`,
                ease: "power2.out",
            });
        };
        if (location.pathname !== "/") {
            navigate("/");
            // wait a frame for DOM to mount then scroll
            setTimeout(doScroll, 50);
        } else {
            doScroll();
        }
        setActive(id);
        setNav(false);
    };

    const goContact = () => {
        navigate("/contact");
        setActive("contact");
        setNav(false);
    };

    const goServices = () => {
        navigate("/services");
        setActive("services");
        setNav(false);
    };

    const goAbout = () => {
        navigate("/about");
        setActive("about");
        setNav(false);
    };

    // Keep active underline in sync with the current route for non-home pages
    useEffect(() => {
        switch (location.pathname) {
            case "/about":
                setActive("about");
                break;
            case "/services":
                setActive("services");
                break;
            case "/contact":
                setActive("contact");
                break;
            default:
                // On home route, let the scroll spy take over highlighting
                setActive("home");
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleSpy = () => {
            const sections = ["home", "about", "services", "contact"];

            sections.forEach((sec) => {
                const element = document.getElementById(sec);
                if (!element) return;

                const rect = element.getBoundingClientRect();
                if (rect.top <= 150 && rect.bottom >= 150) {
                    setActive(sec);
                }
            });

            // Shrink Navbar on Scroll
            setShrink(window.scrollY > 50);
        };

        window.addEventListener("scroll", handleSpy);
        return () => window.removeEventListener("scroll", handleSpy);
    }, []);

    return (
        <div className={`fixed w-full top-0 left-0 z-50 bg-white shadow-sm navbar-default ${shrink ? "navbar-shrink" : ""}`}>
            <div className="flex justify-between items-center h-full max-w-[1240px] mx-auto px-6 text-brand-600 font-semibold">

                {/* Logo and Title */}
                <div className="flex items-center cursor-pointer" onClick={() => handleScroll('home')}>
                    <img
                        src="/images/logo.png"
                        alt="Gesingo Security Technology Logo"
                        className="h-10 w-10 mr-3"
                    />
                    <h1 className="text-2xl font-bold">GESINGO SECURITY TECHNOLOGY</h1>
                </div>

                {/* Desktop Menu */}
                <ul className="hidden md:flex space-x-8">
                    {[
                        { key: "home", onClick: () => handleScroll("home") },
                        { key: "about", onClick: goAbout },
                        { key: "services", onClick: goServices },
                        // { key: "terms", onClick: () => handleScroll("terms") },
                        { key: "contact", onClick: goContact },
                    ].map((item) => (
                        <li
                            key={item.key}
                            onClick={item.onClick}
                            className={`cursor-pointer hover:underline transition ${
                                active === item.key ? "text-brand-accent font-bold underline" : ""
                            }`}
                        >
                            {item.key.charAt(0).toUpperCase() + item.key.slice(1)}
                        </li>
                    ))}
                </ul>

                {/* Mobile Toggle */}
                <div className="md:hidden cursor-pointer" onClick={() => setNav(!nav)}>
                    {nav ? <AiOutlineClose size={25} /> : <AiOutlineMenu size={25} />}
                </div>
            </div>

            {/* Mobile Menu (centered panel with overlay) */}
            {nav && (
                <div className="fixed inset-0 md:hidden z-50">
                    {/* Dim overlay - clicking it closes the menu */}
                    <div className="absolute inset-0 bg-black/30" onClick={() => setNav(false)} />

                    {/* Centered menu card - auto height, not full page */}
                    <div className="absolute left-1/2 top-24 -translate-x-1/2 bg-white rounded-xl shadow-lg w-[85%] max-w-sm px-8 py-8">
                        <ul className="space-y-6 text-lg font-medium text-center">
                            {[
                                { key: "home", onClick: () => handleScroll("home") },
                                { key: "about", onClick: goAbout },
                                { key: "services", onClick: goServices },
                                // { key: "terms", onClick: () => handleScroll("terms") },
                                { key: "contact", onClick: goContact },
                            ].map((item) => (
                                <li
                                    key={item.key}
                                    onClick={item.onClick}
                                    className={`cursor-pointer ${
                                        active === item.key ? "text-brand-accent font-bold underline" : ""
                                    }`}
                                >
                                    {item.key.charAt(0).toUpperCase() + item.key.slice(1)}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;
